"use client"

import { useRef, useEffect, useState, useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Camera, CheckCircle2, AlertCircle, Loader2, Eye, RefreshCw } from "lucide-react"
import { cn } from "@/lib/utils"

interface LivenessCheckProps {
    onSuccess: () => void
    onSkip?: () => void
}

type LivenessState = "idle" | "requesting" | "detecting" | "challenge" | "success" | "error"

export function LivenessCheck({ onSuccess, onSkip }: LivenessCheckProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const animationRef = useRef<number | null>(null)

    const [state, setState] = useState<LivenessState>("idle")
    const [error, setError] = useState<string | null>(null)
    const [faceDetected, setFaceDetected] = useState(false)
    const [blinkCount, setBlinkCount] = useState(0)
    const [eyesClosed, setEyesClosed] = useState(false)
    const [modelsLoaded, setModelsLoaded] = useState(false)

    const requiredBlinks = 2

    // Load face-api models
    const loadModels = useCallback(async () => {
        try {
            const faceapi = await import("@vladmandic/face-api")

            // Load models from CDN
            const MODEL_URL = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model"

            await Promise.all([
                faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
                faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL)
            ])

            setModelsLoaded(true)
            return faceapi
        } catch (err) {
            console.error("Failed to load face detection models:", err)
            throw new Error("Failed to load face detection models")
        }
    }, [])

    // Calculate Eye Aspect Ratio (EAR) for blink detection
    const getEAR = (eye: { x: number; y: number }[]) => {
        // EAR = (||p2-p6|| + ||p3-p5||) / (2 * ||p1-p4||)
        const A = Math.sqrt(Math.pow(eye[1].x - eye[5].x, 2) + Math.pow(eye[1].y - eye[5].y, 2))
        const B = Math.sqrt(Math.pow(eye[2].x - eye[4].x, 2) + Math.pow(eye[2].y - eye[4].y, 2))
        const C = Math.sqrt(Math.pow(eye[0].x - eye[3].x, 2) + Math.pow(eye[0].y - eye[3].y, 2))
        return (A + B) / (2.0 * C)
    }

    // Start camera and detection
    const startCamera = async () => {
        setState("requesting")
        setError(null)

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: 640, height: 480 }
            })

            streamRef.current = stream

            if (videoRef.current) {
                videoRef.current.srcObject = stream
                await videoRef.current.play()
            }

            setState("detecting")

            // Load models and start detection
            const faceapi = await loadModels()
            startDetection(faceapi)
        } catch (err) {
            console.error("Camera error:", err)
            setError("Could not access camera. Please allow camera permissions.")
            setState("error")
        }
    }

    // Face detection loop
    const startDetection = (faceapi: typeof import("@vladmandic/face-api")) => {
        const video = videoRef.current
        const canvas = canvasRef.current

        if (!video || !canvas) return

        const ctx = canvas.getContext("2d")
        if (!ctx) return

        let wasEyesClosed = false
        let currentBlinkCount = 0

        const detect = async () => {
            if (!video || video.paused || video.ended) return

            const detections = await faceapi
                .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
                .withFaceLandmarks(true)

            // Clear canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            if (detections) {
                setFaceDetected(true)
                setState("challenge")

                // Draw face box
                const box = detections.detection.box
                ctx.strokeStyle = "#7ed957"
                ctx.lineWidth = 3
                ctx.strokeRect(box.x, box.y, box.width, box.height)

                // Get eye landmarks (left: 36-41, right: 42-47 in 68-point model)
                const landmarks = detections.landmarks.positions
                const leftEye = landmarks.slice(36, 42)
                const rightEye = landmarks.slice(42, 48)

                const leftEAR = getEAR(leftEye)
                const rightEAR = getEAR(rightEye)
                const avgEAR = (leftEAR + rightEAR) / 2

                // Blink threshold (eyes closed when EAR < 0.2)
                const isBlinking = avgEAR < 0.2
                setEyesClosed(isBlinking)

                // Detect blink (transition from closed to open)
                if (wasEyesClosed && !isBlinking) {
                    currentBlinkCount++
                    setBlinkCount(currentBlinkCount)

                    if (currentBlinkCount >= requiredBlinks) {
                        setState("success")
                        stopCamera()
                        setTimeout(() => onSuccess(), 1500)
                        return
                    }
                }
                wasEyesClosed = isBlinking
            } else {
                setFaceDetected(false)
                if (state !== "detecting") {
                    setState("detecting")
                }
            }

            animationRef.current = requestAnimationFrame(detect)
        }

        detect()
    }

    // Stop camera
    const stopCamera = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current)
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop())
            streamRef.current = null
        }
    }

    // Cleanup on unmount
    useEffect(() => {
        return () => stopCamera()
    }, [])

    // Retry
    const retry = () => {
        stopCamera()
        setBlinkCount(0)
        setFaceDetected(false)
        setEyesClosed(false)
        setError(null)
        setState("idle")
    }

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-2">Liveness Check</h3>
                <p className="text-muted-foreground">
                    We need to verify you're a real person. Please look at the camera and blink {requiredBlinks} times.
                </p>
            </div>

            {/* Camera View */}
            <div className="relative aspect-[4/3] max-w-md mx-auto bg-black rounded-xl overflow-hidden">
                {state === "idle" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/10">
                        <div className="h-20 w-20 rounded-full bg-[#7ed957]/10 flex items-center justify-center mb-4">
                            <Camera className="h-10 w-10 text-[#7ed957]" />
                        </div>
                        <Button onClick={startCamera} className="bg-[#7ed957] hover:bg-[#6bc448]">
                            Start Camera
                        </Button>
                    </div>
                )}

                {state === "requesting" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/10">
                        <Loader2 className="h-12 w-12 text-[#7ed957] animate-spin mb-4" />
                        <p className="text-muted-foreground">Requesting camera access...</p>
                    </div>
                )}

                {state === "error" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/10 p-4">
                        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                        <p className="text-red-500 text-center mb-4">{error}</p>
                        <Button onClick={retry} variant="outline">
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Try Again
                        </Button>
                    </div>
                )}

                {state === "success" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#7ed957]/20">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="h-20 w-20 rounded-full bg-[#7ed957] flex items-center justify-center mb-4"
                        >
                            <CheckCircle2 className="h-12 w-12 text-white" />
                        </motion.div>
                        <p className="text-lg font-semibold text-[#7ed957]">Verification Complete!</p>
                    </div>
                )}

                <video
                    ref={videoRef}
                    className={cn(
                        "w-full h-full object-cover scale-x-[-1]",
                        (state === "idle" || state === "requesting" || state === "error" || state === "success") && "hidden"
                    )}
                    playsInline
                    muted
                />
                <canvas
                    ref={canvasRef}
                    width={640}
                    height={480}
                    className={cn(
                        "absolute inset-0 w-full h-full scale-x-[-1]",
                        (state === "idle" || state === "requesting" || state === "error" || state === "success") && "hidden"
                    )}
                />

                {/* Status overlay */}
                {(state === "detecting" || state === "challenge") && (
                    <div className="absolute bottom-4 left-4 right-4">
                        <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <div className={cn(
                                        "h-3 w-3 rounded-full",
                                        faceDetected ? "bg-[#7ed957]" : "bg-yellow-500 animate-pulse"
                                    )} />
                                    <span className="text-white text-sm">
                                        {faceDetected ? "Face detected" : "Looking for face..."}
                                    </span>
                                </div>
                                {faceDetected && (
                                    <div className="flex items-center gap-1">
                                        <Eye className={cn(
                                            "h-4 w-4",
                                            eyesClosed ? "text-yellow-500" : "text-[#7ed957]"
                                        )} />
                                        <span className="text-white text-sm">
                                            {blinkCount}/{requiredBlinks} blinks
                                        </span>
                                    </div>
                                )}
                            </div>
                            {faceDetected && (
                                <div className="w-full bg-white/20 rounded-full h-2">
                                    <motion.div
                                        className="bg-[#7ed957] h-2 rounded-full"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(blinkCount / requiredBlinks) * 100}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Tips */}
            {(state === "detecting" || state === "challenge") && (
                <div className="max-w-md mx-auto">
                    <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Make sure your face is well-lit</li>
                        <li>• Position your face in the center of the frame</li>
                        <li>• Blink naturally {requiredBlinks} times to complete verification</li>
                    </ul>
                </div>
            )}

            {/* Skip option */}
            {onSkip && state !== "success" && (
                <div className="text-center">
                    <Button variant="ghost" onClick={onSkip} className="text-muted-foreground">
                        Skip for now (submit documents instead)
                    </Button>
                </div>
            )}
        </div>
    )
}
